import * as THREE from 'three';
import { ATTACKS, type AttackStyle } from '../game/combat';
import { PLAYER } from '../game/constants';
import type { WeaponType } from '../game/weapons';

export interface CharacterSpec {
  id: number;
  skin: string;
  hair: string;
  hairStyle: 'spiky' | 'mop' | 'ponytail' | 'bald';
  jersey: string;
  shorts: string;
  socks: string;
  boots: string;
  gloves: string;
  number: number;
  numberColor: string;
  eyes: 'happy' | 'focused' | 'fierce';
  isGoalkeeper: boolean;
}

export interface JointChain {
  upper: THREE.Group;
  lower: THREE.Group;
  tip: THREE.Object3D;
}

export interface CharacterRig {
  root: THREE.Group;
  hips: THREE.Group;
  torso: THREE.Group;
  head: THREE.Group;
  armL: JointChain;
  armR: JointChain;
  legL: JointChain;
  legR: JointChain;
  numberPlate: THREE.Mesh;
}

export const POSE = {
  hipsY: 0,
  hipsPitch: 1,
  hipsRoll: 2,
  hipsYaw: 3,
  torsoPitch: 4,
  torsoYaw: 5,
  torsoRoll: 6,
  headPitch: 7,
  headYaw: 8,
  armLPitch: 9,
  armLOut: 10,
  elbowL: 11,
  armRPitch: 12,
  armROut: 13,
  elbowR: 14,
  legLPitch: 15,
  legLOut: 16,
  kneeL: 17,
  legRPitch: 18,
  legROut: 19,
  kneeR: 20,
} as const;

export const POSE_LENGTH = 21;
export type Pose = Float32Array;

export function createPose(): Pose {
  return new Float32Array(POSE_LENGTH);
}

const BASE_HIPS_Y = 0.72;

export { buildCharacter } from './CharacterModel';

function blendRotation(target: THREE.Euler, x: number, y: number, z: number, k: number): void {
  target.set(
    target.x + (x - target.x) * k,
    target.y + (y - target.y) * k,
    target.z + (z - target.z) * k,
  );
}

export function applyPose(rig: CharacterRig, pose: Pose, smoothing: number, delta: number): void {
  const k = 1 - Math.exp(-smoothing * delta);
  blendRotation(rig.hips.rotation, pose[POSE.hipsPitch], pose[POSE.hipsYaw], pose[POSE.hipsRoll], k);
  blendRotation(rig.torso.rotation, pose[POSE.torsoPitch], pose[POSE.torsoYaw], pose[POSE.torsoRoll], k);
  blendRotation(rig.head.rotation, pose[POSE.headPitch], pose[POSE.headYaw], 0, k);
  rig.hips.position.y += (BASE_HIPS_Y + pose[POSE.hipsY] - rig.hips.position.y) * k;

  blendRotation(rig.armL.upper.rotation, -pose[POSE.armLPitch], 0, pose[POSE.armLOut], k);
  blendRotation(rig.armL.lower.rotation, -pose[POSE.elbowL], 0, 0, k);
  blendRotation(rig.armR.upper.rotation, -pose[POSE.armRPitch], 0, -pose[POSE.armROut], k);
  blendRotation(rig.armR.lower.rotation, -pose[POSE.elbowR], 0, 0, k);

  blendRotation(rig.legL.upper.rotation, -pose[POSE.legLPitch], 0, pose[POSE.legLOut], k);
  blendRotation(rig.legL.lower.rotation, pose[POSE.kneeL], 0, 0, k);
  blendRotation(rig.legR.upper.rotation, -pose[POSE.legRPitch], 0, -pose[POSE.legROut], k);
  blendRotation(rig.legR.lower.rotation, pose[POSE.kneeR], 0, 0, k);
}

export function poseIdle(pose: Pose, t: number): void {
  pose.fill(0);
  const breath = Math.sin(t * 2.4);
  const weight = Math.sin(t * 0.8);
  pose[POSE.hipsY] = breath * 0.018 - 0.035;
  pose[POSE.hipsRoll] = weight * 0.075;
  pose[POSE.torsoRoll] = -weight * 0.095;
  pose[POSE.hipsYaw] = weight * 0.09;
  pose[POSE.kneeL] = 0.12 + Math.max(0, weight) * 0.08;
  pose[POSE.kneeR] = 0.12 + Math.max(0, -weight) * 0.08;
  pose[POSE.headPitch] = -breath * 0.08;
  pose[POSE.torsoPitch] = 0.06 + Math.sin(t * 2.4) * 0.012;
  pose[POSE.armLPitch] = 0.08 - weight * 0.06;
  pose[POSE.armRPitch] = 0.04 + weight * 0.06;
  pose[POSE.armLOut] = 0.28 + breath * 0.025;
  pose[POSE.armROut] = 0.31 + breath * 0.025;
  pose[POSE.elbowL] = 0.35 + weight * 0.06;
  pose[POSE.elbowR] = 0.28 - weight * 0.04;
  pose[POSE.headYaw] = Math.sin(t * 0.7) * 0.3;
  pose[POSE.legLPitch] = 0.03;
  pose[POSE.legRPitch] = -0.03;
}

export function poseReady(pose: Pose, t: number): void {
  pose.fill(0);
  const shuffle = Math.sin(t * 4.5);
  const settle = Math.cos(t * 9);
  pose[POSE.hipsY] = -0.12 + shuffle * shuffle * 0.055;
  pose[POSE.hipsRoll] = shuffle * 0.075;
  pose[POSE.torsoRoll] = -shuffle * 0.09;
  pose[POSE.headYaw] = Math.sin(t * 1.8) * 0.12;
  pose[POSE.torsoPitch] = 0.4 - settle * 0.025;
  pose[POSE.armLPitch] = 0.28 - shuffle * 0.09;
  pose[POSE.armRPitch] = 0.28 + shuffle * 0.09;
  pose[POSE.armLOut] = 0.95 + shuffle * 0.15;
  pose[POSE.armROut] = 0.95 - shuffle * 0.15;
  pose[POSE.elbowL] = 0.55 + shuffle * 0.075;
  pose[POSE.elbowR] = 0.55 - shuffle * 0.075;
  pose[POSE.legLPitch] = 0.2;
  pose[POSE.legRPitch] = 0.2;
  pose[POSE.legLOut] = 0.22;
  pose[POSE.legROut] = 0.22;
  pose[POSE.kneeL] = 0.52 + shuffle * 0.1;
  pose[POSE.kneeR] = 0.52 - shuffle * 0.1;
  pose[POSE.headPitch] = 0.1;
}

export function poseRun(pose: Pose, phase: number, speed01: number, forward = 1, lateral = 0): void {
  pose.fill(0);
  const swing = Math.sin(phase);
  const counter = -swing;
  const recovery = Math.cos(phase);
  const stride = 0.3 + 1.05 * speed01;
  const liftL = Math.max(0, recovery);
  const liftR = Math.max(0, -recovery);
  const armSwing = Math.sin(phase - 0.18) * (0.65 + 0.65 * speed01);
  pose[POSE.hipsY] = (1 - 2 * swing * swing) * 0.07 * speed01 - 0.035;
  pose[POSE.torsoPitch] = 0.18 + 0.16 * speed01;
  pose[POSE.hipsRoll] = swing * 0.16 * speed01;
  pose[POSE.hipsYaw] = swing * 0.24 * speed01;
  pose[POSE.torsoYaw] = -swing * 0.32 * speed01;
  pose[POSE.torsoRoll] = -swing * 0.15 * speed01;
  pose[POSE.headPitch] = -0.12 * speed01 - recovery * recovery * 0.035 * speed01;
  pose[POSE.headYaw] = swing * 0.06 * speed01;
  // Flex during the recovery half-cycle, then extend before the next footfall.
  // Short steps at low speed avoid the old full sprint stride while braking.
  pose[POSE.legLPitch] = swing * stride * forward;
  pose[POSE.legRPitch] = counter * stride * forward;
  pose[POSE.kneeL] = liftL * liftL * (0.35 + 1.4 * speed01) + 0.08;
  pose[POSE.kneeR] = liftR * liftR * (0.35 + 1.4 * speed01) + 0.08;
  pose[POSE.legLOut] = 0.05 + swing * lateral * stride * 0.55;
  pose[POSE.legROut] = 0.05 + swing * lateral * stride * 0.55;
  pose[POSE.torsoRoll] -= lateral * speed01 * 0.2;
  pose[POSE.torsoPitch] *= forward;
  // The shoulders trail the pelvis; the elbows lead the next forward pump.
  pose[POSE.armLPitch] = -armSwing;
  pose[POSE.armRPitch] = armSwing;
  pose[POSE.elbowL] = 0.55 + 0.25 * speed01 + counter * 0.18 * speed01;
  pose[POSE.elbowR] = 0.55 + 0.25 * speed01 + swing * 0.18 * speed01;
  pose[POSE.armLOut] = 0.18 + liftL * 0.1 * speed01;
  pose[POSE.armROut] = 0.18 + liftR * 0.1 * speed01;
}

export function poseWeightTransfer(pose: Pose, forward: number, lateral: number, landing: number): void {
  const forwardWeight = THREE.MathUtils.clamp(forward, -1, 1);
  const pitch = forwardWeight * 0.28;
  const bank = THREE.MathUtils.clamp(lateral, -1, 1) * 0.32;
  const compression = THREE.MathUtils.clamp(landing, -0.25, 1);
  const brace = Math.max(0, -forwardWeight) * 0.18 + Math.max(0, compression) * 0.35;
  pose[POSE.hipsPitch] += pitch * 0.35;
  pose[POSE.torsoPitch] += pitch * 0.65;
  pose[POSE.hipsRoll] -= bank * 0.5;
  pose[POSE.torsoRoll] -= bank * 0.5;
  pose[POSE.headPitch] -= pitch * 0.45;
  pose[POSE.headYaw] += bank * 0.3;
  pose[POSE.hipsY] -= Math.max(0, -pitch) * 0.14 + compression * 0.08;
  pose[POSE.kneeL] += brace;
  pose[POSE.kneeR] += brace;
}

export function poseInjury(pose: Pose, severity: number, phase: number, elapsed: number, moving: boolean, side: number): void {
  const damage = THREE.MathUtils.clamp(severity, 0, 1);
  if (damage === 0) return;
  const injuredLeft = side >= 0;
  const lean = injuredLeft ? 1 : -1;
  const limp = THREE.MathUtils.smoothstep(damage, 0.18, 1);
  const critical = THREE.MathUtils.smoothstep(damage, 0.6, 1);
  const gait = Math.sin(phase) * lean;
  const painfulStep = moving ? Math.max(0, gait) ** 2 : 0;
  const wince = Math.max(0, Math.sin(elapsed * 1.35)) ** 6;
  const protect = damage * (0.45 + limp * 0.55);
  const injuredLeg = injuredLeft ? POSE.legLPitch : POSE.legRPitch;
  const injuredKnee = injuredLeft ? POSE.kneeL : POSE.kneeR;
  const supportLeg = injuredLeft ? POSE.legRPitch : POSE.legLPitch;
  const supportKnee = injuredLeft ? POSE.kneeR : POSE.kneeL;
  const guardPitch = injuredLeft ? POSE.armLPitch : POSE.armRPitch;
  const guardOut = injuredLeft ? POSE.armLOut : POSE.armROut;
  const guardElbow = injuredLeft ? POSE.elbowL : POSE.elbowR;
  const freePitch = injuredLeft ? POSE.armRPitch : POSE.armLPitch;
  const freeOut = injuredLeft ? POSE.armROut : POSE.armLOut;
  const freeElbow = injuredLeft ? POSE.elbowR : POSE.elbowL;

  // Drag the injured foot while the other leg desperately takes the player's weight.
  if (moving) {
    pose[injuredLeg] = pose[injuredLeg] * (1 - limp * 0.86) - limp * 0.12;
    pose[injuredKnee] = pose[injuredKnee] * (1 - limp * 0.75) + limp * 0.14;
    pose[supportLeg] *= 1 - critical * 0.14;
    pose[supportKnee] += painfulStep * limp * 0.3;
  } else {
    pose[injuredLeg] -= limp * 0.08;
    pose[injuredKnee] += limp * 0.1;
    pose[supportKnee] += limp * 0.16;
  }
  pose[POSE.hipsY] -= damage * 0.03 + critical * 0.015 + limp * painfulStep * 0.05;
  pose[POSE.hipsPitch] += critical * 0.05;
  pose[POSE.hipsRoll] += lean * limp * (0.06 + painfulStep * 0.18);
  pose[POSE.torsoRoll] -= lean * protect * (0.1 + painfulStep * 0.25 + wince * 0.16);
  pose[POSE.torsoRoll] += Math.sin(elapsed * 6.2) * critical * 0.07;
  pose[POSE.torsoPitch] += damage * 0.18 + limp * (0.3 + wince * 0.2) + critical * 0.1;
  pose[POSE.torsoYaw] += lean * limp * wince * 0.2;
  pose[POSE.headPitch] += damage * 0.12 + critical * 0.1 + wince * damage * 0.15;
  pose[POSE.headYaw] += lean * wince * protect * 0.3 + Math.sin(elapsed * 5.2) * critical * 0.2;
  // Clutch the ribs continuously, then wrench the other hand onto the lower back in a pain spasm.
  pose[guardPitch] += (0.08 + wince * 0.1 - pose[guardPitch]) * protect;
  pose[guardOut] += (-0.36 - pose[guardOut]) * protect;
  pose[guardElbow] += (1.4 + wince * 0.2 - pose[guardElbow]) * protect;
  const backHold = limp * ((moving ? 0.2 : 0.38) + wince * (moving ? 0.8 : 0.62));
  pose[freePitch] += (-0.95 - pose[freePitch]) * backHold;
  pose[freeOut] += (-0.18 - pose[freeOut]) * backHold;
  pose[freeElbow] += (0.6 - pose[freeElbow]) * backHold;
}

export function poseCrawl(pose: Pose, phase: number, elapsed: number, moving: boolean): void {
  pose.fill(0);
  const step = moving ? Math.sin(phase) : 0;
  const shake = Math.sin(elapsed * 17) * 0.018;
  const reach = moving ? Math.cos(phase) : 0;
  const effort = (1 - Math.cos(elapsed * 2.8)) * 0.5;
  pose[POSE.hipsY] = -0.35 + Math.abs(step) * 0.025 - effort * 0.015;
  pose[POSE.hipsPitch] = 1.1 + shake;
  pose[POSE.hipsRoll] = step * 0.055;
  pose[POSE.torsoPitch] = 0.15 + effort * 0.055;
  pose[POSE.hipsYaw] = step * 0.055;
  pose[POSE.torsoYaw] = -step * 0.1;
  pose[POSE.torsoRoll] = -step * 0.08;
  pose[POSE.headPitch] = -1.02 + Math.sin(elapsed * 2.8) * 0.12;
  pose[POSE.headYaw] = shake * 3;
  pose[POSE.armLPitch] = 1.25 + step * 0.27;
  pose[POSE.armRPitch] = 1.25 - step * 0.27;
  pose[POSE.armLOut] = pose[POSE.armROut] = 0.28;
  pose[POSE.elbowL] = 0.13 + Math.max(0, reach) * 0.55;
  pose[POSE.elbowR] = 0.13 + Math.max(0, -reach) * 0.55;
  pose[POSE.legLPitch] = 1.4 - step * 0.18;
  pose[POSE.legRPitch] = 1.4 + step * 0.18;
  pose[POSE.legLOut] = pose[POSE.legROut] = 0.2;
  pose[POSE.kneeL] = 1.85 + step * 0.14;
  pose[POSE.kneeR] = 1.85 - step * 0.14;
}

export function poseCharge(pose: Pose, power01: number, t: number): void {
  pose.fill(0);
  const wobble = Math.sin(t * 32) * 0.09 * power01;
  pose[POSE.hipsY] = -0.12 - 0.13 * power01;
  pose[POSE.torsoPitch] = -0.2 - 0.35 * power01;
  pose[POSE.hipsPitch] = 0.05;
  pose[POSE.legRPitch] = -0.65 - 0.6 * power01;
  pose[POSE.kneeR] = 0.9 + 0.7 * power01;
  pose[POSE.legLPitch] = 0.24;
  pose[POSE.kneeL] = 0.3;
  pose[POSE.armLPitch] = -0.5 - 0.2 * power01;
  pose[POSE.armRPitch] = -0.4 - 0.3 * power01;
  pose[POSE.armLOut] = 0.5 + 0.2 * power01;
  pose[POSE.armROut] = 0.45 + 0.2 * power01;
  pose[POSE.elbowL] = 0.7;
  pose[POSE.elbowR] = 0.7;
  pose[POSE.headPitch] = 0.12 + wobble;
  pose[POSE.hipsYaw] = -0.18 - 0.12 * power01;
  pose[POSE.torsoYaw] = -0.28 - 0.2 * power01;
  pose[POSE.headYaw] = 0.32;
  pose[POSE.torsoRoll] = -0.1 * power01;
}

export function poseKick(pose: Pose, t: number): void {
  pose.fill(0);
  // Ball release is at 0.30 s of the 0.55 s action; keep the planted leg under the hips.
  const contact = 0.3 / 0.55;
  const windup = THREE.MathUtils.smoothstep(t, 0, 0.32);
  const snap = THREE.MathUtils.smoothstep(t, 0.32, contact);
  const follow = THREE.MathUtils.smoothstep(t, contact + 0.08, 0.8);
  const active = 1 - THREE.MathUtils.smoothstep(t, 0.74, 1);
  const plant = Math.sin(Math.PI * snap);
  const recoil = Math.sin(Math.PI * follow) * active;
  pose[POSE.hipsY] = -0.035 + (-0.038 * windup + 0.045 * snap - 0.025 * plant) * active;
  pose[POSE.torsoPitch] = 0.06 + (0.2 * windup - 0.16 * snap + 0.09 * follow) * active;
  pose[POSE.legRPitch] = -0.03 + (-0.9 * windup + 2.4 * snap - 0.5 * follow) * active;
  pose[POSE.kneeR] = 0.12 + (1.3 * windup * (1 - snap) + 0.3 * recoil) * active;
  pose[POSE.legLPitch] = 0.03 + (0.22 * windup + 0.06 * snap) * active;
  pose[POSE.kneeL] = 0.12 + (0.32 * windup - 0.08 * snap + 0.13 * plant) * active;
  pose[POSE.armLPitch] = 0.05 + (-0.5 * windup + 1.2 * snap) * active;
  pose[POSE.armRPitch] = 0.05 + (0.4 * windup - 1.15 * snap) * active;
  pose[POSE.armLOut] = 0.3 + 0.5 * windup * active;
  pose[POSE.armROut] = 0.3 + 0.4 * windup * active;
  pose[POSE.elbowL] = 0.3 + (0.5 * windup - 0.4 * snap) * active;
  pose[POSE.elbowR] = 0.3 + (0.5 * windup - 0.4 * snap) * active;
  pose[POSE.hipsRoll] = (0.1 * snap - 0.065 * windup) * active;
  pose[POSE.torsoRoll] = (-0.14 * snap + 0.09 * windup) * active;
  pose[POSE.hipsYaw] = (-0.24 * windup + 0.5 * snap - 0.1 * follow) * active;
  pose[POSE.torsoYaw] = (-0.4 * windup + 0.7 * snap - 0.18 * follow) * active;
  pose[POSE.headYaw] = -0.2 * snap * active;
  pose[POSE.headPitch] = (0.14 * windup - 0.22 * snap) * active;
}

export function posePass(pose: Pose, t: number): void {
  const contact = 0.3 / 0.55;
  // The shorter pass releases at 0.27 s / 0.45 s, later in its normalized cycle.
  const phase = t < 0.6 ? t * contact / 0.6 : contact + (t - 0.6) * (1 - contact) / 0.4;
  poseKick(pose, phase);
  const turn = Math.sin(Math.PI * THREE.MathUtils.clamp(t, 0, 1));
  pose[POSE.legRPitch] = (pose[POSE.legRPitch] + 0.03) * 0.68 - 0.03;
  pose[POSE.legROut] = 0.16 * turn;
  pose[POSE.hipsYaw] = -0.45 * turn;
  pose[POSE.torsoYaw] = 0.27 * turn;
  pose[POSE.headYaw] = -0.12 * turn;
  pose[POSE.armLOut] = 0.3 + 0.35 * turn;
  pose[POSE.armROut] = 0.3 + 0.25 * turn;
}

export function posePunch(pose: Pose, t: number, arm: 'L' | 'R'): void {
  pose.fill(0);
  const windup = THREE.MathUtils.smoothstep(t, 0, 0.3);
  const extend = THREE.MathUtils.smoothstep(t, 0.28, 0.55);
  const recover = THREE.MathUtils.smoothstep(t, 0.6, 1);
  const pitch = -1.15 * windup + 3.2 * extend - 1.3 * recover;
  const elbow = 1.55 - 1.5 * (extend - 0.4 * recover);
  const twist = (0.85 * windup - 1.3 * (extend - 0.5 * recover)) * (arm === 'L' ? -1 : 1);
  pose[POSE.hipsPitch] = 0.1;
  pose[POSE.torsoPitch] = 0.14 + 0.14 * extend;
  pose[POSE.hipsYaw] = twist * 0.45;
  pose[POSE.torsoYaw] = twist;
  pose[POSE.hipsY] = -0.04;
  pose[POSE.headPitch] = -0.08 * extend;
  pose[POSE.headYaw] = -twist * 0.3;
  if (arm === 'L') {
    pose[POSE.armLPitch] = pitch;
    pose[POSE.armLOut] = 0.18 + 0.12 * extend;
    pose[POSE.elbowL] = Math.max(0.05, elbow);
    pose[POSE.armRPitch] = 0.7 * windup;
    pose[POSE.elbowR] = 1.15;
  } else {
    pose[POSE.armRPitch] = pitch;
    pose[POSE.armROut] = 0.18 + 0.12 * extend;
    pose[POSE.elbowR] = Math.max(0.05, elbow);
    pose[POSE.armLPitch] = 0.7 * windup;
    pose[POSE.elbowL] = 1.15;
  }
  pose[POSE.legLPitch] = 0.3;
  pose[POSE.legRPitch] = -0.25;
  pose[POSE.kneeL] = 0.3;
  pose[POSE.kneeR] = 0.35;
}

export function poseAttackKick(pose: Pose, t: number): void {
  pose.fill(0);
  const windup = THREE.MathUtils.smoothstep(t, 0, 0.3);
  const snap = THREE.MathUtils.smoothstep(t, 0.28, 0.5);
  const recover = THREE.MathUtils.smoothstep(t, 0.6, 1);
  const kick = -1.5 * windup + 3.4 * snap - 1.4 * recover;
  pose[POSE.hipsY] = -0.02 - 0.06 * windup + 0.08 * snap;
  pose[POSE.hipsPitch] = 0.12 + 0.16 * windup - 0.3 * snap;
  pose[POSE.torsoPitch] = 0.2 + 0.16 * windup - 0.42 * snap + 0.12 * recover;
  pose[POSE.hipsRoll] = 0.18 * snap;
  pose[POSE.torsoRoll] = 0.26 * snap;
  pose[POSE.hipsYaw] = -0.22 * (snap - 0.6 * recover);
  pose[POSE.legRPitch] = kick;
  pose[POSE.kneeR] = 1.5 * windup * (1 - snap * 0.96) + 0.12;
  pose[POSE.legLPitch] = 0.42 * windup + 0.1 * snap;
  pose[POSE.kneeL] = 0.35 + 0.3 * windup;
  pose[POSE.armLPitch] = -0.5 * windup + 1.1 * snap;
  pose[POSE.armRPitch] = 0.6 * windup - 0.7 * snap;
  pose[POSE.armLOut] = 0.5 + 0.35 * windup;
  pose[POSE.armROut] = 0.5 + 0.35 * windup;
  pose[POSE.elbowL] = 0.75 - 0.3 * snap;
  pose[POSE.elbowR] = 0.75 - 0.3 * snap;
  pose[POSE.headPitch] = 0.08 + 0.12 * windup - 0.1 * snap;
}

// Each contact has a different whole-body silhouette; timing still follows the damage windows.
export function poseCombat(pose: Pose, t: number, style: AttackStyle, arm: 'L' | 'R'): void {
  pose.fill(0);
  const contact = ATTACKS[style].hitStart / ATTACKS[style].duration;
  const load = THREE.MathUtils.smoothstep(t, 0, contact * 0.65);
  const strike = THREE.MathUtils.smoothstep(t, contact * 0.65, contact);
  const release = THREE.MathUtils.smoothstep(t, 0.72, 1);
  const active = 1 - release;
  const side = arm === 'L' ? -1 : 1;
  const fist = arm === 'L' ? POSE.armLPitch : POSE.armRPitch;
  const other = arm === 'L' ? POSE.armRPitch : POSE.armLPitch;
  const elbow = arm === 'L' ? POSE.elbowL : POSE.elbowR;
  const out = arm === 'L' ? POSE.armLOut : POSE.armROut;
  pose[POSE.armLOut] = pose[POSE.armROut] = 0.25;
  pose[POSE.kneeL] = pose[POSE.kneeR] = 0.15;

  if (style === 'punch') {
    // Windmill from behind the shoulder, then a straight piston at chest height.
    pose[POSE.hipsY] = (-0.2 * load + 0.15 * strike) * active;
    pose[POSE.hipsPitch] = (-0.25 * load + 0.7 * strike) * active;
    pose[POSE.torsoPitch] = (-0.3 * load + 0.5 * strike) * active;
    pose[POSE.hipsYaw] = side * (-0.7 * load + 1.2 * strike) * active;
    pose[POSE.torsoYaw] = side * (-0.65 * load + 0.8 * strike) * active;
    pose[fist] = (-Math.PI * 1.5 * load + Math.PI * 2.12 * strike) * active;
    pose[elbow] = (0.6 * load * (1 - strike) + 0.07) * active;
    pose[out] = 0.22 + 0.7 * load * (1 - strike);
    pose[other] = (-0.65 * strike + 0.8 * load) * active;
    pose[arm === 'L' ? POSE.elbowR : POSE.elbowL] = 1.2 * active;
    pose[POSE.legLPitch] = (0.75 * load - 0.4 * strike) * active;
    pose[POSE.legRPitch] = (-0.6 * load + 0.9 * strike) * active;
    pose[POSE.kneeL] += 0.5 * load * active;
    pose[POSE.headYaw] = -side * 0.65 * active;
  } else if (style === 'kick') {
    // Both boots launch forward while the torso reclines: a wrestling dropkick.
    const leap = Math.sin(Math.PI * THREE.MathUtils.clamp((t - contact * 0.65) / (0.88 - contact * 0.65), 0, 1));
    pose[POSE.hipsY] = -0.22 * load * (1 - strike) + 0.6 * leap;
    pose[POSE.hipsPitch] = (0.25 * load - 1.55 * strike) * active;
    pose[POSE.torsoPitch] = 0.12 * active;
    pose[POSE.hipsRoll] = side * 0.24 * strike * active;
    pose[POSE.legLPitch] = (0.9 * load - 0.8 * strike) * active;
    pose[POSE.legRPitch] = (1.4 * load - 1.3 * strike) * active;
    pose[POSE.kneeL] = 1.6 * load * (1 - strike) + 0.08;
    pose[POSE.kneeR] = 1.9 * load * (1 - strike) + 0.08;
    pose[POSE.legLOut] = pose[POSE.legROut] = 0.2 * strike * active;
    pose[POSE.armLOut] = pose[POSE.armROut] = 1.25 * strike * active + 0.2;
    pose[POSE.armLPitch] = pose[POSE.armRPitch] = -0.6 * strike * active;
    pose[POSE.headPitch] = 0.65 * strike * active;
  } else if (style === 'uppercut') {
    // Deep crouch, corkscrew ascent, one fist held overhead and tucked knees.
    const flight = Math.sin(Math.PI * THREE.MathUtils.clamp((t - contact * 0.6) / (0.92 - contact * 0.6), 0, 1));
    pose[POSE.hipsY] = -0.38 * load * (1 - strike) + 1.6 * flight;
    pose[POSE.hipsYaw] = side * Math.PI * 2 * THREE.MathUtils.smoothstep(t, contact * 0.6, 0.78);
    pose[POSE.hipsPitch] = (0.4 * load - 0.65 * strike) * active;
    pose[POSE.torsoPitch] = (0.55 * load - 0.25 * strike) * active;
    pose[fist] = (0.1 * load + 2.4 * strike) * active;
    pose[elbow] = 1.5 * load * (1 - strike) + 0.08;
    pose[other] = -0.7 * strike * active;
    pose[POSE.legLPitch] = 1.4 * strike * active;
    pose[POSE.legRPitch] = -0.5 * strike * active;
    pose[POSE.kneeL] = 1.8 * load * active;
    pose[POSE.kneeR] = 1.4 * load * active;
    pose[POSE.headPitch] = -0.45 * strike * active;
  } else if (style === 'spin') {
    // Low, wide helicopter: legs and arms form a readable four-point silhouette.
    pose[POSE.hipsY] = (-0.25 * load + 0.55 * strike) * active;
    pose[POSE.hipsYaw] = side * Math.PI * 4 * THREE.MathUtils.smoothstep(t, contact * 0.6, 0.88);
    pose[POSE.hipsRoll] = Math.sin(t * Math.PI * 4) * 0.45 * strike * active;
    pose[POSE.armLOut] = pose[POSE.armROut] = 1.55 * load * active;
    pose[POSE.legLOut] = pose[POSE.legROut] = 1.05 * strike * active;
    pose[POSE.legLPitch] = 0.3 * strike * active;
    pose[POSE.legRPitch] = -0.3 * strike * active;
    pose[POSE.kneeL] = pose[POSE.kneeR] = 0.1;
    pose[POSE.headPitch] = -0.15 * active;
  } else {
    // Star jump into a full-body belly flop, then hands-and-knees recovery.
    const rise = Math.sin(Math.PI * THREE.MathUtils.clamp(t / contact, 0, 1));
    pose[POSE.hipsY] = 1.9 * rise - 0.38 * strike * active;
    pose[POSE.hipsPitch] = (0.15 * load + 1.4 * strike) * active;
    pose[POSE.torsoPitch] = -0.12 * strike * active;
    pose[POSE.armLPitch] = pose[POSE.armRPitch] = (2.7 * load + 0.2 * strike) * active;
    pose[POSE.armLOut] = pose[POSE.armROut] = (1.15 * load - 0.55 * strike) * active;
    pose[POSE.legLOut] = pose[POSE.legROut] = 0.7 * load * active;
    pose[POSE.kneeL] = pose[POSE.kneeR] = 0.8 * load * (1 - strike);
    pose[POSE.headPitch] = -0.7 * strike * active;
    pose[POSE.kneeL] += 1.2 * Math.sin(Math.PI * release);
    pose[POSE.kneeR] += 1.2 * Math.sin(Math.PI * release);
  }
}

export function poseWeaponHold(pose: Pose, type: WeaponType): void {
  if (type === 'shotgun' || type === 'bazooka') {
    pose[POSE.armRPitch] = -0.676;
    pose[POSE.elbowR] = 1.531;
    pose[POSE.armROut] = -1.39;
    pose[POSE.armLPitch] = 0.924;
    pose[POSE.elbowL] = 0.003;
    pose[POSE.armLOut] = -0.63;
    return;
  }
  pose[POSE.armRPitch] = 0.8;
  pose[POSE.elbowR] = 1.2;
  pose[POSE.armROut] = 0.65;
  if (type === 'plank' || type === 'greatsword' || type === 'warhammer' || type === 'chair') {
    pose[POSE.armLPitch] = 1.2;
    pose[POSE.elbowL] = 1.1;
    pose[POSE.armLOut] = -0.25;
    if (type === 'greatsword' || type === 'warhammer') {
      pose[POSE.torsoPitch] += 0.22;
      pose[POSE.kneeL] = pose[POSE.kneeR] = 0.2;
    }
  }
}

export function poseWeaponSwing(pose: Pose, t: number, type: WeaponType): void {
  pose.fill(0);
  if (type === 'shotgun' || type === 'bazooka') {
    poseWeaponHold(pose, type);
    const recoil = Math.exp(-t * 8) * Math.sin(Math.min(t * 18, Math.PI));
    const brace = Math.sin(Math.PI * THREE.MathUtils.smoothstep(t, 0.12, 0.65));
    pose[POSE.torsoPitch] = -0.3 * recoil;
    pose[POSE.armRPitch] += 0.4 * recoil;
    pose[POSE.hipsY] = -0.045 * recoil - 0.025 * brace;
    pose[POSE.hipsPitch] = 0.08 * recoil;
    pose[POSE.legLPitch] = 0.1;
    pose[POSE.legRPitch] = -0.18;
    pose[POSE.kneeL] = 0.16 + 0.14 * brace;
    pose[POSE.kneeR] = 0.25 + 0.16 * recoil;
    pose[POSE.headPitch] = 0.12 * recoil;
    return;
  }
  const heavy = type === 'greatsword' || type === 'warhammer';
  const wind = THREE.MathUtils.smoothstep(t, 0, heavy ? 0.48 : 0.22);
  const snap = THREE.MathUtils.smoothstep(t, heavy ? 0.49 : 0.24, heavy ? 0.65 : 0.39);
  const recover = THREE.MathUtils.smoothstep(t, 0.7, 1);
  const power = 1 - recover;
  const load = wind * (1 - snap);
  const plant = Math.sin(Math.PI * snap) * power;
  const settle = Math.sin(Math.PI * recover);
  pose[POSE.kneeL] = 0.3 + 0.3 * wind * power;
  pose[POSE.kneeR] = 0.3;
  pose[POSE.legLPitch] = 0.35 * snap * power;
  pose[POSE.legRPitch] = -0.25 * wind * power;
  pose[POSE.headYaw] = -0.3 * (wind - snap) * power;
  pose[POSE.headPitch] = (0.15 * wind - 0.25 * snap) * power;
  pose[POSE.hipsRoll] = (0.08 * wind - 0.14 * snap) * power;
  pose[POSE.legLOut] = 0.12 * power;
  pose[POSE.legROut] = 0.16 * power;
  // The feet carry the weight before the weapon accelerates, then absorb its stop.
  pose[POSE.kneeR] += (heavy ? 0.38 : 0.18) * load + 0.2 * settle;
  pose[POSE.kneeL] += 0.15 * plant + (heavy ? 0.25 : 0.1) * settle;
  pose[POSE.legRPitch] -= (heavy ? 0.15 : 0.07) * load;
  if (type === 'pipe') {
    pose[POSE.hipsYaw] = (0.8 * wind - 1.5 * snap) * power;
    pose[POSE.torsoYaw] = (1.0 * wind - 1.8 * snap) * power;
    pose[POSE.armRPitch] = (0.2 * wind + 1.7 * snap) * power;
    pose[POSE.armROut] = (1.6 * wind - 1.9 * snap) * power;
    pose[POSE.elbowR] = 1.3 - 1.15 * snap * power;
    pose[POSE.armLPitch] = 0.6;
    pose[POSE.elbowL] = 1.1;
    pose[POSE.hipsY] = -0.08 * load - 0.05 * settle;
    pose[POSE.armLPitch] -= 0.55 * snap * power;
    pose[POSE.armLOut] = 0.35 + 0.35 * plant;
  } else {
    const beam = type === 'plank' || type === 'chair' || heavy;
    pose[POSE.hipsY] = ((beam ? 0.4 : 0.15) * wind - (beam ? 0.65 : 0.25) * snap) * power;
    pose[POSE.torsoPitch] = (-0.65 * wind + (beam ? 1.5 : 1.1) * snap) * power;
    pose[POSE.armRPitch] = (3.0 * wind - 1.7 * snap) * power;
    pose[POSE.armROut] = beam ? 0.15 : 0.6;
    pose[POSE.elbowR] = 0.45 + 0.8 * wind * (1 - snap);
    pose[POSE.armLPitch] = beam ? pose[POSE.armRPitch] : -0.7 * wind * power;
    pose[POSE.armLOut] = beam ? -0.22 : 0.7;
    pose[POSE.elbowL] = beam ? pose[POSE.elbowR] : 0.6;
    pose[POSE.torsoRoll] = beam ? 0 : -0.45 * snap * power;
    if (heavy) {
      pose[POSE.torsoPitch] += 0.25 * snap * power;
      pose[POSE.hipsY] -= 0.1 * wind * power;
      if (type === 'warhammer') {
        pose[POSE.kneeL] += 0.25 * snap * power;
        pose[POSE.torsoPitch] += 0.18 * snap * power;
        pose[POSE.hipsY] -= 0.07 * settle;
      }
    }
  }
  pose[POSE.armRPitch] += 0.8 * recover;
  pose[POSE.armROut] += (0.65 - pose[POSE.armROut]) * recover;
  pose[POSE.elbowR] += (1.2 - pose[POSE.elbowR]) * recover;
  if (type === 'plank' || type === 'chair' || heavy) {
    pose[POSE.armLPitch] += (1.2 - pose[POSE.armLPitch]) * recover;
    pose[POSE.armLOut] += (-0.25 - pose[POSE.armLOut]) * recover;
    pose[POSE.elbowL] += (1.1 - pose[POSE.elbowL]) * recover;
  }
}

export function poseWeaponThrow(pose: Pose, t: number): void {
  pose.fill(0);
  const wind = THREE.MathUtils.smoothstep(t, 0, 0.2);
  const release = THREE.MathUtils.smoothstep(t, 0.22, 0.385);
  const recover = 1 - THREE.MathUtils.smoothstep(t, 0.68, 1);
  const plant = Math.sin(Math.PI * release) * recover;
  pose[POSE.hipsYaw] = (0.55 * wind - 0.9 * release) * recover;
  pose[POSE.torsoYaw] = (0.75 * wind - 1.15 * release) * recover;
  pose[POSE.torsoPitch] = (-0.55 * wind + 1.0 * release) * recover;
  pose[POSE.hipsY] = (-0.08 * wind + 0.08 * release - 0.04 * plant) * recover;
  pose[POSE.hipsRoll] = (0.08 * wind - 0.12 * release) * recover;
  pose[POSE.torsoRoll] = -pose[POSE.hipsRoll] * 1.3;
  pose[POSE.headPitch] = (0.15 * wind - 0.25 * release) * recover;
  pose[POSE.headYaw] = (-0.2 * wind + 0.35 * release) * recover;
  pose[POSE.armRPitch] = (3.7 * wind - 2.1 * release) * recover;
  pose[POSE.armROut] = 0.35;
  pose[POSE.elbowR] = 0.3 + (0.9 - 1.1 * release) * recover;
  pose[POSE.armLPitch] = (1.2 * wind - 1.8 * release) * recover;
  pose[POSE.elbowL] = 0.5;
  pose[POSE.legLPitch] = 0.55 * release * recover;
  pose[POSE.legRPitch] = -0.55 * release * recover;
  pose[POSE.kneeL] = 0.12 + 0.23 * recover + 0.22 * plant;
  pose[POSE.kneeR] = 0.12 + 0.38 * wind * (1 - release) * recover;
}

export function poseSlide(pose: Pose, t: number): void {
  pose.fill(0);
  const brake = THREE.MathUtils.smoothstep(t, 0.65, 1);
  const finish = 1 + PLAYER.slideRecovery / PLAYER.slideDuration;
  const stand = THREE.MathUtils.smoothstep(t, 1.35, finish);
  const low = 1 - stand;
  // Hip + upper-leg rotations put the straight boot toward the ball, not overhead.
  pose[POSE.hipsY] = -0.5 * low;
  pose[POSE.hipsPitch] = (-1.05 + 0.55 * brake) * low;
  pose[POSE.hipsRoll] = -0.28 * low;
  pose[POSE.torsoPitch] = (0.25 + 0.4 * brake) * low;
  pose[POSE.torsoYaw] = -0.35 * low;
  pose[POSE.headPitch] = 0.4 * low;
  pose[POSE.legLPitch] = (0.5 + 0.7 * brake) * low;
  pose[POSE.legROut] = 0.4 * low;
  pose[POSE.legRPitch] = (0.4 + 0.15 * brake) * low;
  pose[POSE.kneeL] = 0.06 * low + 1.4 * brake * low;
  pose[POSE.kneeR] = 1.95 * low;
  pose[POSE.armLPitch] = 1.3 * low;
  pose[POSE.armLOut] = 0.45 * low;
  pose[POSE.elbowL] = 0.25 * low;
  pose[POSE.armRPitch] = (-0.9 + 1.4 * brake) * low;
  pose[POSE.armROut] = 0.3 * low;
  pose[POSE.elbowR] = 0.12 * low + 0.9 * stand * (1 - stand);
  pose[POSE.legLPitch] += 0.65 * Math.sin(Math.PI * stand);
}

export function poseDown(pose: Pose, t: number): void {
  pose.fill(0);
  const fall = THREE.MathUtils.smoothstep(t, 0, 0.35);
  const bounce = Math.sin(Math.min(1, t * 2.2) * Math.PI) * 0.16 * (1 - fall);
  const brace = Math.sin(Math.PI * fall);
  pose[POSE.hipsY] = -0.42 * fall + bounce;
  pose[POSE.hipsPitch] = -1.5 * fall;
  pose[POSE.torsoPitch] = 0.5 * fall;
  pose[POSE.legLPitch] = 0.7 * fall;
  pose[POSE.legRPitch] = 0.45 * fall;
  pose[POSE.kneeL] = 0.9 * fall;
  pose[POSE.kneeR] = 0.5 * fall;
  pose[POSE.armLPitch] = -1.1 * fall;
  pose[POSE.armRPitch] = -0.9 * fall;
  pose[POSE.armLOut] = 1.1 * fall;
  pose[POSE.armROut] = 1.0 * fall;
  pose[POSE.headPitch] = 0.55 * fall;
  pose[POSE.headYaw] = Math.sin(t * 12) * 0.25 * fall;
  pose[POSE.torsoRoll] = Math.sin(t * 9) * 0.12 * fall;
  // Arms try to break the fall before the body sprawls; endpoints match get-up.
  pose[POSE.armLPitch] += 0.7 * brace;
  pose[POSE.armRPitch] += 0.45 * brace;
  pose[POSE.elbowL] = 0.65 * brace;
  pose[POSE.elbowR] = 0.35 * brace;
  pose[POSE.kneeR] += 0.35 * brace;
}

export function poseStumble(pose: Pose, t: number, side: number): void {
  const age = THREE.MathUtils.clamp(t, 0, 1);
  const direction = side >= 0 ? 1 : -1;
  const fall = THREE.MathUtils.smoothstep(age, 0.25, 0.48);
  const roll = THREE.MathUtils.smoothstep(age, 0.55, 0.92);
  const flail = THREE.MathUtils.smoothstep(age, 0, 0.1) * (1 - fall);
  const scramble = Math.sin(age * 44);
  poseRun(pose, age * 32, 0.65);
  pose[POSE.armLPitch] += scramble * flail * 1.1;
  pose[POSE.armRPitch] -= scramble * flail * 1.1;
  pose[POSE.armLOut] += flail * 1.3;
  pose[POSE.armROut] += flail * 1.3;
  pose[POSE.torsoPitch] += flail * 0.5;
  pose[POSE.headYaw] += scramble * flail * 0.4;
  // Faceplant, roll over a shoulder, then finish on the back for the existing get-up.
  pose[POSE.hipsY] += (-0.42 - pose[POSE.hipsY]) * fall;
  pose[POSE.hipsPitch] += (1.38 - 2.88 * roll - pose[POSE.hipsPitch]) * fall;
  pose[POSE.hipsRoll] += (direction * Math.sin(roll * Math.PI) * 1.48 - pose[POSE.hipsRoll]) * fall;
  pose[POSE.hipsYaw] *= 1 - fall;
  pose[POSE.torsoPitch] += (0.15 + roll * 0.35 - pose[POSE.torsoPitch]) * fall;
  pose[POSE.torsoRoll] += (Math.sin(9) * 0.12 * roll - pose[POSE.torsoRoll]) * fall;
  pose[POSE.torsoYaw] *= 1 - fall;
  pose[POSE.headPitch] += (0.1 + roll * 0.45 - pose[POSE.headPitch]) * fall;
  pose[POSE.headYaw] += (Math.sin(12) * 0.25 * roll - pose[POSE.headYaw]) * fall;
  pose[POSE.armLPitch] += (1.8 - roll * 2.9 - pose[POSE.armLPitch]) * fall;
  pose[POSE.armRPitch] += (1.6 - roll * 2.5 - pose[POSE.armRPitch]) * fall;
  pose[POSE.armLOut] += (0.5 + roll * 0.6 - pose[POSE.armLOut]) * fall;
  pose[POSE.armROut] += (0.65 + roll * 0.35 - pose[POSE.armROut]) * fall;
  pose[POSE.elbowL] *= 1 - fall;
  pose[POSE.elbowR] *= 1 - fall;
  pose[POSE.legLPitch] += (-0.1 + roll * 0.8 - pose[POSE.legLPitch]) * fall;
  pose[POSE.legRPitch] += (-0.15 + roll * 0.6 - pose[POSE.legRPitch]) * fall;
  pose[POSE.legLOut] *= 1 - fall;
  pose[POSE.legROut] *= 1 - fall;
  pose[POSE.kneeL] += (0.15 + roll * 0.75 - pose[POSE.kneeL]) * fall;
  pose[POSE.kneeR] += (0.1 + roll * 0.4 - pose[POSE.kneeR]) * fall;
}

export function poseUnconscious(pose: Pose, t: number, elapsed: number): void {
  pose.fill(0);
  const age = THREE.MathUtils.clamp(t, 0, 1);
  const buckle = THREE.MathUtils.smoothstep(age, 0, 0.04);
  const fall = THREE.MathUtils.smoothstep(age, 0.025, 0.12);
  const settle = THREE.MathUtils.smoothstep(age, 0.1, 0.2);
  const knees = buckle * (1 - fall);
  const reach = Math.sin(Math.PI * fall);
  const impact = Math.sin(Math.PI * THREE.MathUtils.smoothstep(age, 0.1, 0.18)) * 0.04;
  const breath = Math.sin(elapsed * 1.6) * 0.009 * settle;
  pose[POSE.hipsY] = -0.13 * knees - 0.45 * fall + impact;
  pose[POSE.hipsPitch] = -1.48 * fall;
  pose[POSE.hipsRoll] = 0.08 * fall;
  pose[POSE.torsoPitch] = 0.24 * knees + 0.02 * fall + breath;
  pose[POSE.torsoRoll] = -0.04 * settle;
  pose[POSE.legLPitch] = 0.4 * knees + 0.3 * fall;
  pose[POSE.legRPitch] = 0.3 * knees + 0.16 * fall;
  pose[POSE.kneeL] = 0.95 * knees + 0.32 * fall;
  pose[POSE.kneeR] = 0.8 * knees + 0.18 * fall;
  pose[POSE.legLOut] = 0.1 * fall;
  pose[POSE.legROut] = 0.15 * fall;
  pose[POSE.armLPitch] = -0.65 * fall + 0.55 * reach;
  pose[POSE.armRPitch] = -0.48 * fall + 0.28 * reach;
  pose[POSE.armLOut] = 0.62 * fall;
  pose[POSE.armROut] = 0.82 * fall;
  pose[POSE.elbowL] = 0.24 * fall;
  pose[POSE.elbowR] = 0.12 * fall;
  pose[POSE.headPitch] = 0.16 * fall;
  pose[POSE.headYaw] = 0.4 * settle;
}

export function poseGetup(pose: Pose, t: number, elapsed: number): void {
  const kneel = THREE.MathUtils.smoothstep(t, 0.05, 0.5);
  const stand = THREE.MathUtils.smoothstep(t, 0.48, 1);
  const push = Math.sin(Math.PI * kneel) * (1 - stand);
  const balance = Math.sin(Math.PI * stand);
  poseDown(pose, 1);
  for (let i = 0; i < pose.length; i += 1) pose[i] *= 1 - kneel;
  pose[POSE.hipsY] = -0.42 + 0.16 * kneel + 0.26 * stand;
  pose[POSE.hipsPitch] = -1.5 * (1 - kneel);
  pose[POSE.torsoPitch] = 0.7 * (1 - stand) - Math.sin(Math.PI * stand) * 0.3;
  pose[POSE.torsoRoll] = Math.sin(Math.PI * stand * 2) * 0.22;
  pose[POSE.hipsRoll] = -Math.sin(Math.PI * kneel) * 0.15;
  pose[POSE.legLPitch] = 0.65 * (1 - stand);
  pose[POSE.legRPitch] = -0.5 * kneel * (1 - stand);
  pose[POSE.kneeL] = 1.4 * (1 - stand);
  pose[POSE.kneeR] = 1.6 * kneel * (1 - stand);
  pose[POSE.armLPitch] = 0.7 * kneel * (1 - stand) + 0.35 * balance;
  pose[POSE.armRPitch] = 1.1 * kneel * (1 - stand) + 0.55 * push;
  pose[POSE.armLOut] = 0.35 * balance;
  pose[POSE.armROut] = 0.2 * push + 0.15 * balance;
  pose[POSE.elbowL] = 0.7 * push + 0.3 * balance;
  pose[POSE.elbowR] = 0.45 * kneel * (1 - stand);
  pose[POSE.headPitch] = -0.18 * kneel * (1 - stand);
  pose[POSE.headYaw] = Math.sin(elapsed * 0.7) * 0.14 * stand;
}

export function poseStun(pose: Pose, t: number): void {
  pose.fill(0);
  const sway = Math.sin(t * 5.3);
  pose[POSE.hipsY] = -0.05 + Math.sin(t * 7) * 0.01;
  pose[POSE.hipsRoll] = sway * 0.28;
  pose[POSE.torsoRoll] = Math.sin(t * 5.3 + 0.5) * 0.4;
  pose[POSE.torsoPitch] = 0.16;
  pose[POSE.headPitch] = 0.28 + Math.sin(t * 6) * 0.08;
  pose[POSE.headYaw] = Math.sin(t * 4.2) * 0.6;
  pose[POSE.armLPitch] = 0.35 + Math.sin(t * 5) * 0.1;
  pose[POSE.armRPitch] = 0.3 + Math.sin(t * 5 + 1) * 0.1;
  pose[POSE.armLOut] = 0.42;
  pose[POSE.armROut] = 0.42;
  pose[POSE.elbowL] = 0.95;
  pose[POSE.elbowR] = 0.95;
  pose[POSE.legLPitch] = 0.1;
  pose[POSE.legRPitch] = -0.1;
  pose[POSE.kneeL] = 0.15 + Math.max(0, sway) * 0.3;
  pose[POSE.kneeR] = 0.15 + Math.max(0, -sway) * 0.3;
  pose[POSE.armLOut] += Math.max(0, sway) * 0.2;
  pose[POSE.armROut] += Math.max(0, -sway) * 0.2;
}

export function poseCelebrate(pose: Pose, t: number): void {
  pose.fill(0);
  const cycle = (t / 0.9) % 1;
  const flight = THREE.MathUtils.clamp((cycle - 0.2) / 0.6, 0, 1);
  const jump = 4 * flight * (1 - flight);
  const load = Math.sin(Math.PI * THREE.MathUtils.smoothstep(cycle, 0, 0.2));
  const land = Math.sin(Math.PI * THREE.MathUtils.smoothstep(cycle, 0.8, 1));
  const crouch = 0.6 * load + land;
  const twist = Math.sin(cycle * Math.PI * 2) * jump;
  const pump = Math.sin(cycle * Math.PI * 4) * jump;
  pose[POSE.hipsY] = jump * 0.65 - crouch * 0.08 - 0.035;
  pose[POSE.hipsYaw] = twist * 0.4;
  pose[POSE.torsoYaw] = -twist * 0.35;
  pose[POSE.torsoRoll] = twist * 0.2;
  pose[POSE.torsoPitch] = -0.18 + crouch * 0.25;
  pose[POSE.headPitch] = -0.25;
  pose[POSE.armLPitch] = 2.75 + 0.2 * jump - 0.65 * crouch + 0.22 * pump;
  pose[POSE.armRPitch] = 2.75 + 0.2 * jump - 0.65 * crouch - 0.22 * pump;
  pose[POSE.armLOut] = 0.45 + 0.2 * jump + 0.12 * twist;
  pose[POSE.armROut] = 0.45 + 0.2 * jump - 0.12 * twist;
  pose[POSE.elbowL] = 0.25 + 0.3 * crouch + 0.25 * Math.max(0, pump);
  pose[POSE.elbowR] = 0.25 + 0.3 * crouch + 0.25 * Math.max(0, -pump);
  pose[POSE.legLPitch] = 0.12 + 0.25 * crouch - 0.15 * jump;
  pose[POSE.legRPitch] = 0.12 + 0.25 * crouch + 0.15 * jump;
  pose[POSE.kneeL] = 0.24 + 0.55 * crouch + 0.25 * jump;
  pose[POSE.kneeR] = 0.24 + 0.55 * crouch + 0.25 * jump;
}

export function poseDive(pose: Pose, dir: number, t = 1): void {
  poseReady(pose, 0);
  const extend = THREE.MathUtils.smoothstep(t, 0.06, 0.38);
  const tuck = THREE.MathUtils.smoothstep(t, 0.66, 1);
  const push = Math.sin(Math.PI * THREE.MathUtils.smoothstep(t, 0, 0.24));
  pose[POSE.hipsRoll] = dir * (-1.4 * extend + 0.22 * tuck);
  pose[POSE.headYaw] = dir * 0.18 * extend;
  pose[POSE.headPitch] = 0.1 - 0.25 * extend + 0.18 * tuck;
  pose[POSE.hipsY] = -0.12 - 0.07 * push + 0.08 * extend - 0.08 * tuck;
  pose[POSE.torsoRoll] = dir * (-0.2 * extend + 0.08 * tuck);
  pose[POSE.torsoPitch] = 0.4 - 0.22 * extend + 0.18 * tuck;
  pose[POSE.armLPitch] = 0.2 + (dir > 0 ? 1.6 : 1.3) * extend - 0.35 * tuck;
  pose[POSE.armRPitch] = 0.2 + (dir < 0 ? 1.6 : 1.3) * extend - 0.35 * tuck;
  pose[POSE.armLOut] = 0.95 + (dir > 0 ? 0.35 : -1.2) * extend + (dir > 0 ? -0.4 : 0.2) * tuck;
  pose[POSE.armROut] = 0.95 + (dir < 0 ? 0.35 : -1.2) * extend + (dir < 0 ? -0.4 : 0.2) * tuck;
  pose[POSE.elbowL] = pose[POSE.elbowR] = 0.55 - 0.4 * extend + 0.6 * tuck;
  pose[POSE.legLPitch] = 0.2 + (dir > 0 ? -0.35 : 0.45) * extend + (dir > 0 ? 0.55 : 0.2) * tuck;
  pose[POSE.legRPitch] = 0.2 + (dir < 0 ? -0.35 : 0.45) * extend + (dir < 0 ? 0.55 : 0.2) * tuck;
  pose[POSE.kneeL] = 0.52 + (dir > 0 ? -0.4 : 0.35) * extend + (dir > 0 ? 0.9 : 0.4) * tuck;
  pose[POSE.kneeR] = 0.52 + (dir < 0 ? -0.4 : 0.35) * extend + (dir < 0 ? 0.9 : 0.4) * tuck;
  pose[POSE.hipsYaw] = dir * 0.12 * extend * (1 - tuck);
  pose[POSE.torsoYaw] = -dir * 0.2 * extend * (1 - tuck);
  pose[POSE.kneeL] += (dir > 0 ? 0.28 : 0.12) * push;
  pose[POSE.kneeR] += (dir < 0 ? 0.28 : 0.12) * push;
  pose[POSE.legLOut] = 0.22 + 0.18 * extend * (1 - tuck);
  pose[POSE.legROut] = 0.22 + 0.18 * extend * (1 - tuck);
}

export function poseCatch(pose: Pose, t: number): void {
  pose.fill(0);
  const absorb = Math.sin(Math.PI * THREE.MathUtils.clamp(t / 0.4, 0, 1));
  const secure = THREE.MathUtils.smoothstep(t, 0.12, 0.5);
  pose[POSE.hipsY] = -0.12 - absorb * 0.2;
  pose[POSE.torsoRoll] = Math.sin(t * 4) * 0.025;
  pose[POSE.torsoPitch] = 0.34 + absorb * 0.25;
  pose[POSE.armLPitch] = 1.22 - 0.17 * secure;
  pose[POSE.armRPitch] = 1.22 - 0.17 * secure;
  pose[POSE.armLOut] = 0.3;
  pose[POSE.armROut] = 0.3;
  pose[POSE.elbowL] = 0.85 + absorb * 0.25;
  pose[POSE.elbowR] = 0.85 + absorb * 0.25;
  pose[POSE.legLPitch] = 0.24;
  pose[POSE.legRPitch] = 0.24;
  pose[POSE.kneeL] = 0.6 + 0.25 * absorb;
  pose[POSE.kneeR] = 0.6 + 0.25 * absorb;
  pose[POSE.legLOut] = 0.2;
  pose[POSE.legROut] = 0.2;
  pose[POSE.headPitch] = 0.16;
}

export function poseThrow(pose: Pose, t: number): void {
  pose.fill(0);
  const windup = THREE.MathUtils.smoothstep(t, 0, 0.4);
  const throwPhase = THREE.MathUtils.smoothstep(t, 0.4, 0.62);
  const recover = 1 - THREE.MathUtils.smoothstep(t, 0.72, 1);
  const plant = Math.sin(Math.PI * throwPhase);
  const armPitch = -1.4 * windup + 3.4 * throwPhase;
  pose[POSE.hipsYaw] = 0.45 * windup - 0.6 * throwPhase;
  pose[POSE.hipsY] = -0.12 * windup + 0.18 * throwPhase;
  pose[POSE.headYaw] = -0.2 * windup + 0.3 * throwPhase;
  pose[POSE.torsoYaw] = 0.8 * windup - 1.1 * throwPhase;
  pose[POSE.torsoPitch] = -0.15 * windup + 0.25 * throwPhase;
  pose[POSE.armRPitch] = armPitch;
  pose[POSE.elbowR] = 1.2 - 1.1 * throwPhase;
  pose[POSE.armLPitch] = 0.9 * windup - 1.2 * throwPhase;
  pose[POSE.armLOut] = 0.3 + 0.3 * windup;
  pose[POSE.armROut] = 0.2;
  pose[POSE.elbowL] = 0.8;
  pose[POSE.headPitch] = -0.1;
  pose[POSE.legLPitch] = 0.2 + 0.25 * throwPhase;
  pose[POSE.legRPitch] = -0.15 - 0.2 * windup;
  pose[POSE.kneeL] = 0.3 + 0.18 * plant;
  pose[POSE.kneeR] = 0.3 * windup * (1 - throwPhase);
  pose[POSE.hipsRoll] = -0.08 * throwPhase;
  pose[POSE.torsoRoll] = 0.12 * throwPhase;
  for (let i = 0; i < pose.length; i += 1) pose[i] *= recover;
}

export function poseHammer(pose: Pose, t: number): void {
  pose.fill(0);
  const raise = THREE.MathUtils.smoothstep(t, 0, 0.35);
  const smash = THREE.MathUtils.smoothstep(t, 0.42, 0.6);
  const recover = THREE.MathUtils.smoothstep(t, 0.65, 1);
  const load = raise * (1 - smash);
  const impact = Math.sin(Math.PI * smash);
  pose[POSE.armRPitch] = 2.6 * raise - 4.4 * smash + 1.3 * recover;
  pose[POSE.armROut] = 0.35;
  pose[POSE.elbowR] = 0.6 + 0.6 * raise - 1.0 * smash;
  pose[POSE.armLPitch] = 1.6 * raise - 2.2 * smash;
  pose[POSE.elbowL] = 0.8 + 0.8 * raise;
  pose[POSE.torsoPitch] = -0.2 * raise + 0.55 * smash - 0.2 * recover;
  pose[POSE.hipsY] = 0.04 * raise - 0.1 * smash;
  pose[POSE.legLPitch] = 0.35 * smash;
  pose[POSE.kneeL] = 0.4 * smash;
  pose[POSE.kneeR] = 0.5 * raise;
  pose[POSE.hipsPitch] = -0.12 * load + 0.2 * smash;
  pose[POSE.headPitch] = 0.12 * load - 0.18 * smash;
  pose[POSE.armLOut] = 0.3;
  pose[POSE.legRPitch] = -0.22 * load;
  pose[POSE.kneeL] += 0.25 * impact;
  for (let i = 0; i < pose.length; i += 1) pose[i] *= 1 - recover;
}

export function poseBomb(pose: Pose, t: number): void {
  pose.fill(0);
  const windup = THREE.MathUtils.smoothstep(t, 0, 0.35);
  const release = THREE.MathUtils.smoothstep(t, 0.35, 0.55);
  const recover = 1 - THREE.MathUtils.smoothstep(t, 0.72, 1);
  pose[POSE.armRPitch] = -1.6 * windup + 3.2 * release;
  pose[POSE.elbowR] = 1.3 - 1.2 * release;
  pose[POSE.torsoPitch] = -0.2 * windup + 0.3 * release;
  pose[POSE.armLPitch] = 0.8 * windup - 1.1 * release;
  pose[POSE.armLOut] = 0.5;
  pose[POSE.armROut] = 0.3;
  pose[POSE.elbowL] = 0.9;
  pose[POSE.headPitch] = -0.12;
  pose[POSE.hipsY] = -0.08 * windup + 0.08 * release;
  pose[POSE.hipsYaw] = 0.4 * windup - 0.65 * release;
  pose[POSE.torsoYaw] = 0.6 * windup - 0.95 * release;
  pose[POSE.headYaw] = -pose[POSE.torsoYaw] * 0.3;
  pose[POSE.legLPitch] = 0.35 * release;
  pose[POSE.legRPitch] = -0.3 * windup;
  pose[POSE.kneeL] = 0.2 + 0.15 * Math.sin(Math.PI * release);
  pose[POSE.kneeR] = 0.2 + 0.25 * windup * (1 - release);
  for (let i = 0; i < pose.length; i += 1) pose[i] *= recover;
}
